import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { CurrentOpenMenu, SearchFacet } from '@/pages/SearchPageSimplified/types';
import { FilterAction } from '@/types/SearchFacet';
import {
    getArchivedSearchFacets,
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
    getSelectedCategoryFacetValue,
    getUpcomingSearchFacets,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { getSearchUrlWithParams } from '@/utils/urls';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { modifyParamsRemovePage, modifyParamsWithTaxonomySelected, parseQueryParams } from '@/utils/queryParams';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';

const ORIGIN_FACET_ID = 'F2';

type Props = {
    isArchivedSearch: boolean;
};

export const OriginFilter = ({ isArchivedSearch }: Props) => {
    const { formatMessage } = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);

    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const currentCategoryId = queryParams[paramTypes.CATEGORY_ID];

    const nestedOriginFacet = useAppSelector((state) =>
        getSelectedCategoryFacetValue(state, isArchivedSearch, FacetTypes.ORIGIN_ID)
    );
    const rawArchivedSearchFacets = useAppSelector(getRawArchivedSearchFacets);
    const rawUpcomingSearchFacets = useAppSelector(getRawUpcomingSearchFacets);
    const rawFacets = isArchivedSearch ? rawArchivedSearchFacets : rawUpcomingSearchFacets;
    const navigateWithParams = useNavigateSearchPageWithParams();

    const handleNavigation = useCallback(
        (event: MouseEvent<HTMLAnchorElement | HTMLInputElement>, params: any) => {
            event.preventDefault();

            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: CurrentOpenMenu.AllFilters,
                filterAction: FilterAction.OriginChanged,
                params,
                preventScrollReset: true,
            });
        },
        [navigateWithParams]
    );

    const buildOriginCategoryHref = (categoryId: string, remove?: boolean) => {
        let newParams = modifyParamsRemovePage(queryParams);
        if (remove) {
            delete newParams[paramTypes.CATEGORY_ID];
        } else {
            newParams = modifyParamsWithTaxonomySelected(newParams, FacetTypes.CATEGORY_ID, categoryId, rawFacets);
        }

        return newParams;
    };

    const archivedSearchFacets = useAppSelector(getArchivedSearchFacets);
    const upcomingFacets = useAppSelector(getUpcomingSearchFacets);
    const facets = isArchivedSearch ? archivedSearchFacets : upcomingFacets;

    const originFacet = useMemo(() => {
        let originFacet: SearchFacet;

        facets.forEach((facet) => {
            switch (facet.id) {
                case ORIGIN_FACET_ID:
                    originFacet = facet as SearchFacet;
                    break;

                default:
                    break;
            }
        });

        return originFacet;
    }, [facets]);

    if (!originFacet) {
        return null;
    }
    const { categories } = originFacet;

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={formatMessage({ id: 'search_facet_title_F2' })}
            open
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor="#fff"
                onChange={(isExpanded) => setIsExpanded(isExpanded)}
                textCutoffHeight={200}
            >
                <div
                    className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                        'overflow-y-auto': isExpanded,
                    })}
                >
                    {/* L1 Categories */}
                    {Boolean(!nestedOriginFacet.categories) &&
                        categories?.facets[0]?.id !== currentCategoryId &&
                        categories.facets.map((option) => (
                            <SubtleMonochromeLink
                                href={getSearchUrlWithParams(buildOriginCategoryHref(option.id))}
                                key={option.id}
                                onClick={(event) => handleNavigation(event, buildOriginCategoryHref(option.id))}
                            >
                                {option.name}
                            </SubtleMonochromeLink>
                        ))}
                    {/* ALL OTHER LEVELS */}
                    {Boolean(nestedOriginFacet.categories) && (
                        <>
                            <H5 className="text-link-primary">{nestedOriginFacet.name}</H5>
                            {nestedOriginFacet.categories.map((option) => (
                                <SubtleMonochromeLink
                                    href={getSearchUrlWithParams(buildOriginCategoryHref(option.id))}
                                    key={option.id}
                                    onClick={(event) => handleNavigation(event, buildOriginCategoryHref(option.id))}
                                >
                                    {option.name}
                                </SubtleMonochromeLink>
                            ))}
                        </>
                    )}
                </div>
            </ViewMore>
        </Accordion>
    );
};
